import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Privacy screen: blurs student photos and reduces names to initials.
 *
 * For the moments when the dashboard is on the projector or a parent is
 * standing at the desk. It is a display filter, nothing more — the data is
 * already loaded, so this is about what the room can see, not about access
 * control. Real protection is the login and the owner-scoped API.
 *
 * The choice lives in localStorage rather than on the account: whether the
 * screen is mirrored is a property of where you are sitting, not of who
 * you are.
 */
const STORAGE_KEY = 'td.privacyScreen';

interface PrivacyValue {
  privacy: boolean;
  setPrivacy: (on: boolean) => void;
  toggle: () => void;
}

const PrivacyContext = createContext<PrivacyValue | null>(null);

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const [privacy, setPrivacyState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'on';
    } catch {
      return false;
    }
  });

  const setPrivacy = useCallback((on: boolean) => {
    setPrivacyState(on);
    try {
      localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
    } catch {
      // Private browsing with storage blocked — the toggle still works for
      // this session, it just won't be remembered.
    }
  }, []);

  const toggle = useCallback(() => setPrivacy(!privacy), [privacy, setPrivacy]);

  // Ctrl/Cmd + Shift + P, because reaching for a menu is too slow when
  // someone is already walking over.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        toggle();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return (
    <PrivacyContext.Provider value={{ privacy, setPrivacy, toggle }}>
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacy(): PrivacyValue {
  const ctx = useContext(PrivacyContext);
  if (!ctx) throw new Error('usePrivacy must be used inside PrivacyProvider');
  return ctx;
}
